import React, { useMemo, useRef, useState, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Format inline par CLASSE (identité centralisée) : <span class="text-terracotta">
const Inline = Quill.import('blots/inline');
class TerracottaBlot extends Inline {
  static formats() { return true; }
}
TerracottaBlot.blotName = 'terracotta';
TerracottaBlot.tagName = 'span';
TerracottaBlot.className = 'text-terracotta';
Quill.register('formats/terracotta', TerracottaBlot, true);

// On conserve uniquement les formats utiles (dont "terracotta"), pour que la
// sanitisation interne de Quill ne supprime pas la mise en évidence.
const FORMATS = ['bold', 'italic', 'underline', 'list', 'link', 'terracotta'];

/**
 * Éditeur riche réutilisable.
 * Bouton "Terracotta Mitonner" (couleur officielle du thème, pas de choix libre)
 * qui applique / retire la classe .text-terracotta sur la portion sélectionnée.
 */
export default function RichTextEditor({ value, onChange, placeholder, testId, label }) {
  const quillRef = useRef(null);

  // État LOCAL synchrone pendant la frappe : évite le round-trip asynchrone via
  // l'état du parent (setState batché) qui fait sauter le curseur / perdre le texte.
  const [local, setLocal] = useState(value || '');
  const lastEmitted = useRef(value || '');

  // Resynchronise UNIQUEMENT lors d'un vrai changement externe (chargement, reset),
  // pas quand le parent renvoie notre propre saisie.
  useEffect(() => {
    const incoming = value || '';
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming;
      setLocal(incoming);
    }
  }, [value]);

  const handleChange = (html) => {
    lastEmitted.current = html;
    setLocal(html);
    if (onChange) onChange(html);
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['terracotta'],
        ['clean'],
      ],
      handlers: {
        terracotta: function () {
          const range = this.quill.getSelection();
          if (!range || range.length === 0) return;
          const current = this.quill.getFormat(range);
          this.quill.format('terracotta', !current.terracotta);
        },
      },
    },
  }), []);

  return (
    <div data-testid={testId || 'rich-text-editor'}>
      {label ? <p className="text-sm font-medium text-[#3C4A41] mb-1">{label}</p> : null}
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={local}
        onChange={handleChange}
        modules={modules}
        formats={FORMATS}
        placeholder={placeholder}
      />
      <p className="text-xs text-[#5C6A61] mt-1">
        Astuce : sélectionnez un mot ou un montant puis cliquez sur « A » (terracotta Mitonner) pour le mettre en évidence.
      </p>
    </div>
  );
}
